import { c as _c } from "react/compiler-runtime";
import type { DialogContentProps } from '@radix-ui/react-dialog';
import * as RadixDialog from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import type { DialogSize } from '../dialog.types';
type ContainerProps = {
  isConfirmation: boolean;
  isCentered: boolean;
  onClose: () => void;
  size: DialogSize;
  open: boolean;
  preventEscapeKeyDown?: boolean;
} & DialogContentProps;
const Container = forwardRef<HTMLDivElement, ContainerProps>((t0, ref) => {
  const $ = _c(24);
  let children;
  let isCentered;
  let isConfirmation;
  let onClose;
  let onEscapeKeyDown;
  let preventEscapeKeyDown;
  let props;
  let size;
  if ($[0] !== t0) {
    const {
      children: t1,
      onEscapeKeyDown: t2,
      onClose: t3,
      size: t4,
      isConfirmation: t5,
      isCentered: t6,
      preventEscapeKeyDown: t7,
      open,
      ...t8
    } = t0;
    children = t1;
    onEscapeKeyDown = t2;
    onClose = t3;
    size = t4;
    isConfirmation = t5;
    isCentered = t6;
    preventEscapeKeyDown = t7;
    props = t8;
    $[0] = t0;
    $[1] = children;
    $[2] = isCentered;
    $[3] = isConfirmation;
    $[4] = onClose;
    $[5] = onEscapeKeyDown;
    $[6] = preventEscapeKeyDown;
    $[7] = props;
    $[8] = size;
  } else {
    children = $[1];
    isCentered = $[2];
    isConfirmation = $[3];
    onClose = $[4];
    onEscapeKeyDown = $[5];
    preventEscapeKeyDown = $[6];
    props = $[7];
    size = $[8];
  }
  let t1;
  if ($[9] !== onClose || $[10] !== onEscapeKeyDown || $[11] !== preventEscapeKeyDown) {
    t1 = event => {
      if (preventEscapeKeyDown) {
        event.preventDefault();
      } else {
        if (onEscapeKeyDown) {
          onEscapeKeyDown(event);
        } else {
          onClose();
        }
      }
    };
    $[9] = onClose;
    $[10] = onEscapeKeyDown;
    $[11] = preventEscapeKeyDown;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const handleEscapeKeyDown = t1;
  let t2;
  if ($[13] !== isCentered || $[14] !== isConfirmation || $[15] !== size) {
    t2 = containerStyles({
      size,
      isConfirmation,
      isCentered
    });
    $[13] = isCentered;
    $[14] = isConfirmation;
    $[15] = size;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== children || $[18] !== handleEscapeKeyDown || $[19] !== onClose || $[20] !== props || $[21] !== ref || $[22] !== t2) {
    t3 = <RadixDialog.Content {...props} className={t2} onEscapeKeyDown={handleEscapeKeyDown} onPointerDownOutside={onClose} ref={ref}>{children}</RadixDialog.Content>;
    $[17] = children;
    $[18] = handleEscapeKeyDown;
    $[19] = onClose;
    $[20] = props;
    $[21] = ref;
    $[22] = t2;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  return t3;
});
const containerStyles = cva(['fixed flex flex-col left-0 right-0 mx-auto', 'bg-primary shadow-lg isolate overflow-visible', 'border border-tertiary border-solid rounded-xl', 'data-[state=open]:animate-scaleIn', 'data-[state=closed]:animate-scaleOut', 'transition-[width] duration-[240ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none'], {
  variants: {
    size: {
      // iOS Safari's vh is the viewport with its toolbar hidden, and the toolbar never hides while a
      // dialog locks scrolling, so phones size to dvh or the footer lands under the toolbar.
      'full-screen': 'inset-0 w-[calc(100vw-40px)] h-[calc(100dvh-40px)] top-5 md:h-[calc(100vh-40px)]',
      wide: 'w-[960px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
      large: 'w-[800px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
      default: 'w-[650px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]',
      compact: 'w-[500px] max-w-[calc(100vw-32px)] max-h-[calc(100dvh-48px)] md:max-h-[calc(100vh-48px)]'
    },
    isConfirmation: {
      true: 'z-confirmation-dialog',
      false: 'z-dialog'
    },
    isCentered: {
      true: 'top-1/2 -translate-y-1/2',
      false: ''
    }
  },
  compoundVariants: [{
    isCentered: false,
    size: ['wide', 'large', 'default', 'compact'],
    className: 'top-6'
  }],
  defaultVariants: {
    size: 'default',
    isConfirmation: false,
    isCentered: false
  }
});
export default Container;